import { progress } from "components/notification/subscribe"
import { updateValidation } from "components/table/table-automation"
import { ApiResult } from "data/interface/api"
import { diff_obj } from "data/lib/diff"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"
import { local, setlocal } from "data/lib/local-json"
import { logEvent } from "data/lib/log-event"
import { getFolderMigrations, getMigrations, updatedServices } from "data/lib/migrations"
import { push_undo } from "data/lib/undo"
import loadingService from 'data/loading'
import notificationService from "data/notification"
import validationService from "data/validate"
import Client from "data/_client"
import _ from "lodash"
import moment from "moment"
import { propertyChannel } from "data/websocket/sharedChannel"
import socketService, * as eventType from "data/socket"
import calcService from 'data/calc'
// private module logic //

const { fetchCalc } = calcService
export type Property = {
  propertyid: number
  folderid: number
  term: number
  valuationdetail: object
  code: string
  leasetypedetail: Array<{name: string}>
  calcdetail: {status: string}
}
type PropertyStreamState = ApiResult<Property[]> & {
  id: "property"
  current: number | null
}

const id = "property"
const getStoredPropertyId = () => JSON.parse(localStorage.property || null)
const initialState: PropertyStreamState = { id, state: "init", value: [], current: getStoredPropertyId() }
const propertyStream = new BehaviorSubject<PropertyStreamState>(initialState)
const propertyEventStream = new BehaviorSubject<{type: string, payload: any}>({type: "", payload: {}})
let propertyMap = {}

const current = () => propertyStream.getValue()

function clear(): void {
  propertyStream.next({ id, state: "ok", value: [], current: null })
  propertyMap = {}
}

function get(propertyId?: number): Property {
  return propertyMap[propertyId || getStoredPropertyId()]
}

function setCalcPending(propertyId: number) {
  let property = _.cloneDeep(get(propertyId))
  property = _.merge(property, {calcdetail: {status: 'Pending', completePercent: 0}})
  propertyStream.next({ id, current: current().current, state: "ok", value: _.unionBy([property], current().value, "propertyid") })
}

function refresh(): Promise<any> {
  const currentValue = propertyStream.getValue().value
  const folderid = _.get(currentValue, '0.folderid')
  if (_.isNil(folderid)) return Promise.resolve()
  return fetchByFolderId(folderid)
}

function fetchSummary(propertyId:number){
  const jsreport = process.env.JSREPORT_API
  return Client.get(`query/propertysummarybar/${propertyId}`, { host: jsreport })
  .then((summary) => {
    return summary
  })
  .catch((err: {message: string}) => {
    return err
  })
}

function fetchProperties(propertyIds: number[]): Promise<Property[] | { message: string }> {
  propertyStream.next({ id, current: current().current, state: "busy", value: current().value })
  return Client.get(`property?propertyid=in.${propertyIds.join(',')}`)
    .then((properties: Property[]) => {
      _.forEach(properties, p => propertyMap[p.propertyid] = p)
      // merge results unique by propertyid
      propertyStream.next({ id, current: current().current, state: "ok", value: _.unionBy(properties, current().value, "propertyid") })
      updateValidation()
      return properties
    })
    .catch((err: {message: string}) => {
      propertyStream.next({ id, current: current().current, state: "error", error: err, value: current().value })
      notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
      return err
    })
}


function fetchProperty(propertyId: number): Promise<Property | { message: string }> {
  propertyStream.next({ id, current: current().current, state: "busy", value: current().value })

  return Client.get(`property?propertyid=eq.${propertyId}`)
    .then(async (properties: Property[]) => {
      _.forEach(properties, p => propertyMap[p.propertyid] = p)
      // merge results unique by propertyid
      //await validationService.fetchAllByParent('property', propertyId)
      propertyStream.next({ id, current: current().current, state: "ok", value: _.unionBy(properties, current().value, "propertyid") })
      updateValidation()
      return properties[0]
    })
    .catch((err: {message: string}) => {
      propertyStream.next({ id, current: current().current, state: "error", error: err, value: current().value })
      notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
      return err
    })
}

function fetchByFolderId(folderId: number, showLoading: boolean = true, limit: number = 0, offset: number = 0): Promise<Property[] | { message: string }> {
  showLoading && loadingService.toggleLoading(true, 'fetchByFolderId');
  propertyStream.next({ id, current: current().current, state: "busy", value: current().value })

  let url = `property?folderid=eq.${folderId}`
  if(limit && offset >= 0) {
    url = `property?folderid=eq.${folderId}&limit=${limit}&offset=${offset}`
  }
  return getFolderMigrations(folderId)
  .then(async (updates) => {
    await Client.post(`validate/folder/${folderId}`, {channelid: folderId}).catch(console.log)
    return Client.get(url)
      .then((properties: Property[]) => {
        _.forEach(properties, p => propertyMap[p.propertyid] = p)
        _.forEach(propertyMap, p => p.folderid !== folderId && delete propertyMap[p.propertyid])
        updatedServices(updates)

        const currentValue = _.filter(current().value, {folderid: folderId})
        // merge results unique by propertyid
        propertyStream.next({ id, current: current().current, state: "ok", value: _.unionBy(properties, currentValue, "propertyid") })
        updateValidation()
        showLoading && loadingService.toggleLoading(false, 'fetchByFolderId');
        return properties
      })
  })
  .catch((err: {message: string}) => {
    showLoading && loadingService.toggleLoading(false, 'fetchByFolderId');
    propertyStream.next({ id, current: current().current, state: "error", error: err, value: current().value })
    notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
    return err
  })
}

function accumulateByFolderId(folderId: number, showLoading: boolean = true, limit: number = 0, offset: number = 0): Promise<Property[] | { message: string }> {
  showLoading && loadingService.toggleLoading(true, 'fetchByFolderId');
  propertyStream.next({ id, current: current().current, state: "busy", value: current().value })
  const currentValue = current().value

  let url = `property?folderid=eq.${folderId}`
  if(limit && offset >= 0) {
    url = `property?folderid=eq.${folderId}&limit=${limit}&offset=${offset}`
  }
  return getFolderMigrations(folderId)
  .then(async (updates) => {
    await Client.post(`validate/folder/${folderId}`, {channelid: folderId}).catch(console.log)
    return Client.get(url)
      .then((properties: Property[]) => {
        _.forEach(properties, p => propertyMap[p.propertyid] = p)
        _.forEach(propertyMap, p => p.folderid !== folderId && delete propertyMap[p.propertyid])
        updatedServices(updates)

        // merge results unique by propertyid
        propertyStream.next({ id, current: current().current, state: "ok", value: _.unionBy(properties, currentValue, "propertyid") })
        updateValidation()
        showLoading && loadingService.toggleLoading(false, 'fetchByFolderId');
        return properties
      })
  })
  .catch((err: {message: string}) => {
    showLoading && loadingService.toggleLoading(false, 'fetchByFolderId');
    propertyStream.next({ id, current: current().current, state: "error", error: err, value: current().value })
    notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
    return err
  })
}

async function patch(property: Property): Promise<Property | { message: string }> {
  const current = propertyStream.getValue()
  const propertyid: number = property.propertyid
  const currentProp = _.find(current.value, {propertyid})
  const changeset = _.omit(diff_obj(property, currentProp), 'activitydetail')
  if(_.isEmpty(changeset)) return new Promise(() => current.value)
  push_undo({
    undofunc: () => patch(currentProp),
    redofunc: () => patch(property)
  })

  propertyStream.next({ id, current: current.current, state: "ok", value: _.unionBy([property], current.value, "propertyid") })

  return Client.patch(`property?propertyid=eq.${propertyid}`, changeset)
    .then((properties: Property[]) => {
      _.forEach(properties, p => {
        propertyMap[p.propertyid] = p
        fetchProperty(p.propertyid)
      })
      return properties[0]
    })
    .catch((err: {message: string}) => {
      // on error overwrite optimistic update
      propertyStream.next({ id, current: current.current, state: "error", error: err, value: current.value })
      notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
      return err
    })
}

function isCodeUnique(folderId: number, code: string): Promise<boolean | {message: string}> {
  return Client.get(`property?folderid=eq.${folderId}&code=eq.${code}`)
    .then(res => {
      if(res.length === 0) return true
      else return false
    })
    .catch((err: {message: string}) => {
      return err
    })
}

export type PropertyType = "Multifamily" | "Commercial" | "Hospitality" | "Mixed-Use"
type CreateProperyPayload_t = {
  src: "property"
  setup: "create"
  type: PropertyType
  folderId: number
  _synchronous: true
  code?: string
  area?: number
  units?: number
  inflation?: number
}

function createProperty(folderId: number, type: PropertyType = 'Commercial', details: {code: string, area?: number, units?: number, inflation?: number}): Promise<Property | { message: string }> {
  propertyStream.next({ id, current: current().current, state: "busy", value: current().value })

  const payload: CreateProperyPayload_t= {
    src: "property",
    setup: "create",
    type: ["Multifamily", "Hospitality", "Mixed-Use"].includes(type) ? type : "Commercial",
    folderId,
    _synchronous: true,
    ...details
  }

  // const {code, area, units, inflation} = details || {}

  // if(code) payload.code = code

  const destination = {folderid: folderId}
  let correlationid
  notificationService.notify({ type: "info", message: "Creating property", dismiss: 3000 })
  return logEvent('create-property', {destination})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_create_property`, payload, {headers:{correlationid}}))
    .then((properties: Property[]) => {
      _.forEach(properties, p => propertyMap[p.propertyid] = p)
      // merge results unique by propertyid
      propertyStream.next({ id, current: current().current, state: "ok", value: _.unionBy(properties, current().value, "propertyid") })
      propertyEventStream.next({ type: "PROPERTY_CREATED", payload: properties[0] })
      selectProperty(properties[0].propertyid)
      return properties[0]
    })
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('create-property-failed', {destination}, correlationid)

      // on error overwrite optimistic update
      propertyStream.next({ id, current: current().current, state: "error", error: res.error, value: current().value })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text") || _.get(res, "message"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function copyProperty(propertyId: number, folderId: number, code: string, area?: number): Promise<void> {

  const payload = {
    src: "property",
    setup: "copy",
    folderId,
    propertyId,
    code,
    area,
    userId: _.get(JSON.parse(localStorage.getItem("auth.profile") || "{}"), "user_id"),
    _synchronous: true
  }

  notificationService.notify({ type: "info", message: "Property copy initiated", dismiss: 3000 })
  // const component = progress('user', (event, update, unsubscribe) => {
  //   if (event.msg.correlationid != correlationid) return
  //   const progress = event.msg.progress*100|0
  //   if(progress == 100) {
  //     unsubscribe()
  //     // TODO: putting this here makes it less robust against page refresh
  //     fetchProperty(event.msg.propertyId)
  //     .then(property => {
  //       notificationService.notify({ type: "info", message: `Copy of ${propertyMap[propertyId]?.code} complete.`, dismiss: 3000 })
  //       socketService.newEvent({ eventType: eventType.PROPERTY_COPY_FINISHED_EVENT, event: {property} })
  //     })

  //   }
  //   update(progress)
  // })
  // notificationService.notify({ type: "info", message: `Copying ${propertyMap[propertyId]?.code}`, dismiss: 3000, component })
  const source = {propertyid: propertyId}
  const destination = {folderid: folderId}
  let correlationid
  return logEvent('copy-property', {source, destination})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_copy_property`, payload, {headers: {correlationid}}))
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('copy-property-failed', {source, destination}, correlationid)
      // on error overwrite optimistic update
      propertyStream.next({ id, current: current().current, state: "error", error: res.error, value: current().value })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function moveProperty(propertyId: number, destinationId: number): Promise<void> {

  const payload = {
    destinationId,
    propertyId,
    _synchronous: true
  }

  notificationService.notify({ type: "info", message: "Property move initiated", dismiss: 3000 })

  const source = {propertyid: propertyId}
  const destination = {folderid: destinationId}
  let correlationid
  return logEvent('move-property', {source, destination})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_move_property`, payload, {headers: {correlationid}}))
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('copy-property-failed', {source, destination}, correlationid)
      // on error overwrite optimistic update
      propertyStream.next({ id, current: current().current, state: "error", error: res.error, value: current().value })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function copies(folderId: number, propertyId: number, count: number): Promise<void> {
  const property = _.find(propertyStream.getValue().value, {propertyid: propertyId})
  const payload = {
    src: "property",
    setup: "copy",
    folderId,
    propertyId,
    unique: true,
    count,
    code: property.code,
    userId: _.get(JSON.parse(localStorage.getItem("auth.profile") || "{}"), "user_id"),
    _synchronous: true
  }

  notificationService.notify({ type: "info", message: "Property copy initiated; you will be notified upon completion.", dismiss: 3000 })

  const source = {propertyid: propertyId}
  const destination = {folderid: folderId}
  let correlationid
  return logEvent('copy-property', {source, destination})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_copy_property`, payload, {headers: {correlationid}}))
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('copy-property-failed', {source, destination}, correlationid)
      // on error overwrite optimistic update
      propertyStream.next({ id, current: current().current, state: "error", error: res.error, value: current().value })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function deleteProperty(propertyId: number): Promise<boolean | { message: string }> {
  const originVal = propertyStream.getValue().value

  if (current().current == propertyId) {
    const message = "You cannot delete your current property"
    notificationService.notify({ type: "warn", message, dismiss: 2000 })
    return Promise.resolve(false)
  }

  // filter out the deleted propertyid
  propertyStream.next({ id, current: current().current, state: "ok", value: _.filter(current().value, p => p.propertyid !== propertyId) })

  const payload = {
    propertyId,
    _synchronous: true
  }

  // update the db
  const target = {propertyid: propertyId}
  const source = {folderid: get(propertyId).folderid}
  let correlationid
  return logEvent('delete-property', {target, source})
    .then((cid) => correlationid = cid)
    .then(() => Client.post(`msg/svc_delete_property`, payload, {headers:{correlationid}}))
    .then((properties: Property) => {
      const deletedProperty = get(propertyId)
      // revalidate any properties that might have had the same code as the deleted property
      validationService.validateAfterChange(deletedProperty, deletedProperty, propertyStream.getValue().value)
      // remove deleted property from local cache
      _.forEach(properties, p => delete propertyMap[p.propertyid])
      // update already happened optimistically
      return true
    })
    .catch((res: { error: { text: string } }) => { // these are old error signatures coming from the py helpers that we kept for backward compatibility with legacy
      logEvent('delete-property-failed', {target, source}, correlationid)

      // on error overwrite optimistic update
      propertyStream.next({ id, current: current().current, state: "error", error: res.error, value: originVal })
      notificationService.notify({ type: "danger", message: _.get(res, "error.text"), dismiss: 5000 })
      return _.get(res, "error.text")
    })
}

function selectProperty(propertyid: number) {
  const property = _.find(current().value, {propertyid})
  if (!property) return console.warn(`Property ${propertyid} not found in loaded data.`)
  //
  // Update recent properties in local storage
  let recentProps = local('recentProperties', [])
  const time = moment().utc().local();
  const zoneName = moment.tz.guess();
  const timezone = moment.tz(zoneName).zoneAbbr();
  let match = _.find(recentProps, {id: property['propertyid']});
  if (match) _.set(match, 'time', time)
  if (!match) recentProps?.push({id: property['propertyid'], organizationId: property['organizationid'], time, timezone})
  setlocal("recentProperties", recentProps)
  //
  setlocal("property", propertyid, false)
  setlocal("property_type", property.type)
  propertyChannel.handlePropertySelection(propertyid)
  propertyStream.next({ id, state: "ok", value: current().value, current: propertyid})
  fetchCalc(propertyid)
  getMigrations(propertyMap[propertyid], true)
    .then(updatedServices)
}

function clearSelectedProperty() {
  setlocal("property", null, false)
  setlocal("property_type", null)
  propertyStream.next({ id, state: "ok", value: current().value, current: null})
}

// public module interface //

const propertyService = {
  stream: () => propertyStream.asObservable(),
  value: () => propertyStream.getValue().value,
  current: () => _.find(propertyStream.getValue().value, {propertyid: propertyStream.getValue().current}),
  eventStream: () => propertyEventStream.asObservable(),
  clear,
  copies,
  get,
  setCalcPending,
  getStoredPropertyId,
  fetchProperties,
  fetchProperty,
  fetchByFolderId,
  fetchSummary,
  accumulateByFolderId,
  patch,
  isCodeUnique,
  createProperty,
  copyProperty,
  deleteProperty,
  moveProperty,
  selectProperty,
  clearSelectedProperty,
  refresh
}
export default propertyService
